import { defineStore } from 'pinia';
import { useNuxtApp } from '#app';
import axios from "axios";
import type {
    AddressType, ApiOffersResponse,
    BuildingHeight,
    BuildingType,
    GoodsType, HouseInsuranceCompany, HouseInsuranceForm, HouseInsuranceOfferRequest,
    InsuredSumType, OfferType,
    ResistanceStructure, SeismicRisk
} from "~/types/House";

export const useHouseStore = defineStore('house', {
    state: () => ({
        buildingTypeList: [] as BuildingType[],
        _fetchBuildingTypeListPromise: null as Promise<any> | null,
        buildingHeightList: [] as BuildingHeight[],
        _fetchBuildingHeightListPromise: null as Promise<any> | null,
        addressTypeList: [] as AddressType[],
        _fetchAddressTypeListPromise: null as Promise<any> | null,
        resistanceStructureList: [] as ResistanceStructure[],
        _fetchResistanceStructureListPromise: null as Promise<any> | null,
        insuredSumTypeList: [] as InsuredSumType[],
        _fetchInsuredSumTypeListPromise: null as Promise<any> | null,
        goodsTypeList: [] as GoodsType[],
        _fetchGoodsTypeListPromise: null as Promise<any> | null,
        seismicRiskList: [] as SeismicRisk[],
        _fetchSeismicRiskListPromise: null as Promise<any> | null,
        offerTypeList: [] as OfferType[],
        _fetchOfferTypeListPromise: null as Promise<any> | null,
        _storeHouseInsurancePromise: null as Promise<any> | null,
        insuranceCompanies: [] as HouseInsuranceCompany[],
        _fetchInsuranceCompaniesPromise: null as Promise<any> | null,
    }),
    actions: {
        async fetchBuildingTypeList() {
            if (this._fetchBuildingTypeListPromise) return this._fetchBuildingTypeListPromise;

            this._fetchBuildingTypeListPromise = (async () => {
                try {
                    const response = await axios.get(`${import.meta.env.VITE_API_BASE_URL}/api/v1/house-insurance-details/building-type-list`);
                    if (response.data.data) {
                        this.buildingTypeList = response.data.data.map((buildingType: BuildingType) => ({
                            id: buildingType.id,
                            name: buildingType.name,
                        }));
                    }
                } catch (error) {
                    throw error;
                } finally {
                    this._fetchBuildingTypeListPromise = null;
                }
            })();

            return this._fetchBuildingTypeListPromise;
        },
        async getBuildingTypeList(): Promise<BuildingType[]> {
            if (this.buildingTypeList.length === 0) {
                await this.fetchBuildingTypeList();
            }
            return this.buildingTypeList;
        },
        async fetchBuildingHeightList() {
            if (this._fetchBuildingHeightListPromise) return this._fetchBuildingHeightListPromise;

            this._fetchBuildingHeightListPromise = (async () => {
                try {
                    const response = await axios.get(`${import.meta.env.VITE_API_BASE_URL}/api/v1/house-insurance-details/building-height-list`);
                    if (response.data.data) {
                        this.buildingHeightList = response.data.data.map((buildingHeight: BuildingHeight) => ({
                            id: buildingHeight.id,
                            name: buildingHeight.name,
                        }));
                    }
                } catch (error) {
                    throw error;
                } finally {
                    this._fetchBuildingHeightListPromise = null;
                }
            })();

            return this._fetchBuildingHeightListPromise;
        },
        async getBuildingHeightList(): Promise<BuildingHeight[]> {
            if (this.buildingHeightList.length === 0) {
                await this.fetchBuildingHeightList();
            }
            return this.buildingHeightList;
        },
        async fetchAddressTypeList() {
            if (this._fetchAddressTypeListPromise) return this._fetchAddressTypeListPromise;

            this._fetchAddressTypeListPromise = (async () => {
                try {
                    const response = await axios.get(`${import.meta.env.VITE_API_BASE_URL}/api/v1/house-insurance-details/address-type-list`);
                    if (response.data.data) {
                        this.addressTypeList = response.data.data.map((addressType: AddressType) => ({
                            id: addressType.id,
                            name: addressType.name,
                        }));
                    }
                } catch (error) {
                    throw error;
                } finally {
                    this._fetchAddressTypeListPromise = null;
                }
            })();

            return this._fetchAddressTypeListPromise;
        },
        async getAddressTypeList(): Promise<AddressType[]> {
            if (this.addressTypeList.length === 0) {
                await this.fetchAddressTypeList();
            }
            return this.addressTypeList;
        },
        async fetchResistanceStructureList() {
            if (this._fetchResistanceStructureListPromise) return this._fetchResistanceStructureListPromise;

            this._fetchResistanceStructureListPromise = (async () => {
                try {
                    const response = await axios.get(`${import.meta.env.VITE_API_BASE_URL}/api/v1/house-insurance-details/resistance-structure-list`);
                    if (response.data.data) {
                        this.resistanceStructureList = response.data.data.map((resistanceStructure: ResistanceStructure) => ({
                            id: resistanceStructure.id,
                            name: resistanceStructure.name,
                        }));
                    }
                } catch (error) {
                    throw error;
                } finally {
                    this._fetchResistanceStructureListPromise = null;
                }
            })();

            return this._fetchResistanceStructureListPromise;
        },
        async getResistanceStructureList(): Promise<ResistanceStructure[]> {
            if (this.resistanceStructureList.length === 0) {
                await this.fetchResistanceStructureList();
            }
            return this.resistanceStructureList;
        },
        async fetchInsuredSumTypeList() {
            if (this._fetchInsuredSumTypeListPromise) return this._fetchInsuredSumTypeListPromise;

            this._fetchInsuredSumTypeListPromise = (async () => {
                try {
                    const response = await axios.get(`${import.meta.env.VITE_API_BASE_URL}/api/v1/house-insurance-details/insured-sum-type-list`);
                    if (response.data.data) {
                        this.insuredSumTypeList = response.data.data.map((insuredSumType: InsuredSumType) => ({
                            id: insuredSumType.id,
                            name: insuredSumType.name,
                        }));
                    }
                } catch (error) {
                    throw error;
                } finally {
                    this._fetchInsuredSumTypeListPromise = null;
                }
            })();

            return this._fetchInsuredSumTypeListPromise;
        },
        async getInsuredSumTypeList(): Promise<InsuredSumType[]> {
            if (this.insuredSumTypeList.length === 0) {
                await this.fetchInsuredSumTypeList();
            }
            return this.insuredSumTypeList;
        },
        async fetchGoodsTypeList(excludedGoodTypeCodes: string[] = []) {
            if (this._fetchGoodsTypeListPromise) return this._fetchGoodsTypeListPromise;

            this._fetchGoodsTypeListPromise = (async () => {
                try {
                    const response = await axios.get(`${import.meta.env.VITE_API_BASE_URL}/api/v1/house-insurance-goods/type-list?excluded_good_type_codes=${excludedGoodTypeCodes.join(',')}`);
                    if (response.data.data) {
                        this.goodsTypeList = response.data.data.map((goodsType: GoodsType) => ({
                            id: goodsType.id,
                            name: goodsType.name,
                        }));
                    }
                } catch (error) {
                    throw error;
                } finally {
                    this._fetchGoodsTypeListPromise = null;
                }
            })();

            return this._fetchGoodsTypeListPromise;
        },
        async getGoodsTypeList(excludedGoodTypeCodes: string[] = []): Promise<GoodsType[]> {
            if (this.goodsTypeList.length === 0) {
                await this.fetchGoodsTypeList(excludedGoodTypeCodes);
            }
            return this.goodsTypeList;
        },
        async fetchSeismicRiskList() {
            if (this._fetchSeismicRiskListPromise) return this._fetchSeismicRiskListPromise;

            this._fetchSeismicRiskListPromise = (async () => {
                try {
                    const response = await axios.get(`${import.meta.env.VITE_API_BASE_URL}/api/v1/house-insurance-details/seismic-risk-list`);
                    if (response.data.data) {
                        this.seismicRiskList = response.data.data.map((seismicRisk: SeismicRisk) => ({
                            id: seismicRisk.id,
                            name: seismicRisk.name,
                        }));
                    }
                } catch (error) {
                    throw error;
                } finally {
                    this._fetchSeismicRiskListPromise = null;
                }
            })();

            return this._fetchSeismicRiskListPromise;
        },
        async getSeismicRiskList(): Promise<SeismicRisk[]> {
            if (this.seismicRiskList.length === 0) {
                await this.fetchSeismicRiskList();
            }
            return this.seismicRiskList;
        },
        async fetchOfferTypeList() {
            if (this._fetchOfferTypeListPromise) return this._fetchOfferTypeListPromise;

            this._fetchOfferTypeListPromise = (async () => {
                try {
                    const response = await axios.get(`${import.meta.env.VITE_API_BASE_URL}/api/v1/house-insurance-offers/type-list`);
                    if (response.data.data) {
                        this.offerTypeList = response.data.data.map((offerType: OfferType) => ({
                            id: offerType.id,
                            name: offerType.name,
                        }));
                    }
                } catch (error) {
                    throw error;
                } finally {
                    this._fetchOfferTypeListPromise = null;
                }
            })();

            return this._fetchOfferTypeListPromise;
        },
        async getOfferTypeList(): Promise<OfferType[]> {
            if (this.offerTypeList.length === 0) {
                await this.fetchOfferTypeList();
            }
            return this.offerTypeList;
        },
        async storeHouseInsurance(houseInsuranceForm: HouseInsuranceForm, accessToken: string) {
            if (this._storeHouseInsurancePromise) return this._storeHouseInsurancePromise;

            this._storeHouseInsurancePromise = (async () => {
                try {
                    const response = await axios.post(`${import.meta.env.VITE_API_BASE_URL}/api/v1/house-insurance-details`, houseInsuranceForm, {
                        headers: {
                            'Authorization': `Bearer ${accessToken}`,
                        }
                    });
                    if (response.data.data) {
                        return response.data.data;
                    }
                } catch (error) {
                    throw error;
                } finally {
                    this._storeHouseInsurancePromise = null;
                }
            })();

            return this._storeHouseInsurancePromise;
        },
        async fetchInsuranceCompanies() {
            if (this._fetchInsuranceCompaniesPromise) return this._fetchInsuranceCompaniesPromise;

            this._fetchInsuranceCompaniesPromise = (async () => {
                try {
                    const response = await axios.get(`${import.meta.env.VITE_API_BASE_URL}/api/v1/house-insurance-companies`);
                    if (response.data.data) {
                        this.insuranceCompanies = response.data.data.map((insuranceCompany: HouseInsuranceCompany) => ({
                            id: insuranceCompany.id,
                            name: insuranceCompany.name,
                            code: insuranceCompany.code,
                            logo: insuranceCompany.logo,
                        }));
                    }
                } catch (error) {
                    throw error;
                } finally {
                    this._fetchInsuranceCompaniesPromise = null;
                }
            })();

            return this._fetchInsuranceCompaniesPromise;
        },
        async getInsuranceCompanies(): Promise<HouseInsuranceCompany[]> {
            if (this.insuranceCompanies.length === 0) {
                await this.fetchInsuranceCompanies();
            }
            return this.insuranceCompanies;
        },
        async fetchOffer(houseInsuranceOfferRequest: HouseInsuranceOfferRequest, accessToken: string): Promise<ApiOffersResponse|null> {
            try {
                const response = await axios.post(`${import.meta.env.VITE_API_BASE_URL}/api/v1/house-insurance-offers`, houseInsuranceOfferRequest, {
                    headers: {
                        'Authorization': `Bearer ${accessToken}`,
                    }
                });
                return response.data.data;
            } catch (error) {
                return null;
            }
        }
    },
    persist: {
        storage: persistedState.sessionStorage,
        paths: [
            'buildingTypeList',
            'buildingHeightList',
            'addressTypeList',
            'resistanceStructureList',
            'insuredSumTypeList',
            'goodsTypeList',
            'seismicRiskList',
            'offerTypeList',
            'insuranceCompanies'
        ],
        key: 'houseStore',
    }
});
